import { ReactElement, useMemo, useCallback, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useRouter } from 'next/router'
import { websiteFormatArticleList, formatPostsCategory } from '@platform/utils'
import slugify from 'slugify'
import {
  WebsiteLayout,
  CategoryTitleButton,
  FeaturedArticle,
  StayInTheLoop,
  ArticleList,
  HEADER_THEMES,
  useCleanHeader,
  useUpdateHeader,
  WebsiteSeo,
} from '@platform/website-components'
import { ArrowDown } from '@icons'
import { SearchBar, Button, Spinner, MenuDropdown } from '@platform/ui-kit'
import { QueryClient, dehydrate } from '@tanstack/react-query'
import {
  useGetBlogMainPageQuery,
  useInfiniteGetAllBlogCategoriesWithNestedPostsQuery,
  CategoryWithPostsAttributes,
  useInfiniteGetAllBlogPostsQuery,
  SortingDirection,
  useGetAllBlogCategoriesWithNestedPostsQuery,
  useGetAllBlogPostsQuery,
  useInfiniteGetAllBlogCategoriesQuery,
  useGetAllBlogCategoriesQuery,
  Locale,
} from '@platform/data-access'
import type { GetServerSideProps } from 'next'
import * as Sentry from '@sentry/nextjs'

import styles from './index.module.scss'

const SEO = {
  title: 'Blog // Olby',
  description:
    'Insights on international career, remote work, productivity, work-life balance, engineering, team management and leadership.',
}

const FIRST_PAGE = 1
const INITIAL_PAGE_SIZE = 3
const CATEGORIES_PAGE_SIZE = 6
const MINIMUM_POSTS_PER_CATEGORY = 1
const LATEST_NEWS_PAGE_SIZE = 6
const PAGE_SIZE = 20

const POSTS_BY_CATEGORY_PAGINATION = {
  sort: [{ updatedAt: SortingDirection.Desc }],
  pagination: {
    page: FIRST_PAGE,
    pageSize: INITIAL_PAGE_SIZE,
  },
  minimumLength: MINIMUM_POSTS_PER_CATEGORY,
}

const GET_BLOG_MAIN_PAGE_PAYLOAD = { input: { locale: Locale.En } }
const GET_ALL_BLOG_CATEGORIES_WITH_NESTED_POSTS_PAYLOAD = {
  input: {
    pagination: {
      page: FIRST_PAGE,
      pageSize: CATEGORIES_PAGE_SIZE,
    },
    posts: { ...POSTS_BY_CATEGORY_PAGINATION, locale: Locale.En },
  },
}
const GET_ALL_BLOG_POSTS_PAYLOAD = {
  input: {
    sort: [{ createdAt: SortingDirection.Desc }],
    pagination: {
      page: FIRST_PAGE,
      pageSize: LATEST_NEWS_PAGE_SIZE,
    },
  },
}

const GET_ALL_BLOG_CATEGORIES = {
  input: {
    pagination: {
      page: FIRST_PAGE,
      pageSize: PAGE_SIZE,
    },
    posts: POSTS_BY_CATEGORY_PAGINATION,
  },
}

export const Blog = () => {
  const router = useRouter()
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [dropdownCategoriesPage] = useState(FIRST_PAGE)

  const { data } = useGetBlogMainPageQuery(GET_BLOG_MAIN_PAGE_PAYLOAD, { enabled: false })
  const featuredPost = data?.getBlogMainPage.data?.featuredPost?.post
  const categoryName = slugify(featuredPost?.category?.name || '', { lower: true })

  // Query to get categories only on More Categories Button
  const { data: categoriesData, isLoading: isLoadingCategories } =
    useInfiniteGetAllBlogCategoriesQuery(GET_ALL_BLOG_CATEGORIES, {
      getNextPageParam: lastPage => {
        if (
          lastPage.getAllBlogCategoriesWithNestedPosts.pagination?.page !==
          lastPage.getAllBlogCategoriesWithNestedPosts.pagination?.pageCount
        ) {
          return {
            input: {
              pagination: {
                page: FIRST_PAGE,
                pageSize: PAGE_SIZE,
              },
              guides: POSTS_BY_CATEGORY_PAGINATION,
            },
          }
        }
        return undefined
      },
      enabled: false,
    })

  const {
    data: categoriesAndArticles,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteGetAllBlogCategoriesWithNestedPostsQuery(
    GET_ALL_BLOG_CATEGORIES_WITH_NESTED_POSTS_PAYLOAD,
    {
      getNextPageParam: lastPage => {
        const lastPageNumber = Number(
          lastPage?.getAllBlogCategoriesWithNestedPosts.pagination?.page
        )

        if (
          lastPageNumber <
          Number(lastPage.getAllBlogCategoriesWithNestedPosts.pagination?.pageCount)
        ) {
          return {
            input: {
              pagination: {
                page: lastPageNumber + 1,
                pageSize: CATEGORIES_PAGE_SIZE,
              },
              posts: POSTS_BY_CATEGORY_PAGINATION,
            },
          }
        }
        return undefined
      },
      enabled: false,
    }
  )

  const { data: latestNews } = useInfiniteGetAllBlogPostsQuery(GET_ALL_BLOG_POSTS_PAYLOAD, {
    enabled: false,
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm<{ search: string }>({ mode: 'onSubmit', defaultValues: { search: '' } })

  const onSearchClear = () => {
    reset()
  }

  const onSubmit = handleSubmit(data => {
    if (dirtyFields.search) {
      router.push({
        pathname: '/blog/search-results',
        query: { searchPosts: data.search },
      })
    }
  })

  const { highlightedCategories, moreOptions } = useMemo(() => {
    const formattedCategories = formatPostsCategory(
      categoriesData?.pages[dropdownCategoriesPage - FIRST_PAGE].getAllBlogCategoriesWithNestedPosts
        .data
    )
    return {
      moreOptions: {
        items: [{ items: formattedCategories?.moreOptionsCategories }],
      },
      highlightedCategories: formattedCategories?.highlightedCategories,
    }
  }, [categoriesData, dropdownCategoriesPage])

  const latestNewsSectionPosts = useMemo(() => {
    if (!latestNews) {
      return []
    }
    return latestNews.pages.flatMap(page => page.getAllBlogPosts.data)
  }, [latestNews])

  const sectionsAndPosts = useMemo(() => {
    if (!categoriesAndArticles) {
      return []
    }
    const currentCategories = categoriesAndArticles?.pages?.flatMap<
      CategoryWithPostsAttributes | undefined | null
    >(page => page.getAllBlogCategoriesWithNestedPosts.data)

    const formattedCategoriesPath = currentCategories?.map(category => ({
      ...category,
      id: slugify(category?.name || '', { lower: true }),
      posts: websiteFormatArticleList(category?.posts || []),
    }))

    return formattedCategoriesPath
  }, [categoriesAndArticles])

  const formatLatestNewsPosts = useMemo(() => {
    return websiteFormatArticleList(latestNewsSectionPosts || [])
  }, [latestNewsSectionPosts])

  const onLoadMore = useCallback(() => {
    fetchNextPage()
  }, [fetchNextPage])

  const TriggerButton = useMemo(
    () => (
      <Button endAdornment={ArrowDown} className={styles['trigger-button']}>
        More categories
      </Button>
    ),
    []
  )

  useUpdateHeader(contentRef, HEADER_THEMES.NEUTRAL_BACKGROUND)
  useCleanHeader()

  const featuredImage = featuredPost?.featuredImage?.image?.url
  const overwrittenMediumImage =
    featuredPost?.featuredImage?.image?.formats?.overwrittenMedium?.url || ''

  return (
    <div className={styles['main-container']} ref={contentRef}>
      <WebsiteSeo seo={SEO} />
      <section className={styles['search-container']}>
        <h1 className={styles['title']}>Blog</h1>
        <form onSubmit={onSubmit} className={styles['search-bar']}>
          <SearchBar
            id="search"
            onSearchClear={onSearchClear}
            variant="search-with-submit"
            placeholder="Search for articles"
            label="Search for article"
            hiddenLabel
            size="medium"
            {...register('search')}
          />
        </form>
      </section>
      <FeaturedArticle
        title={featuredPost?.title || ''}
        description={featuredPost?.resume || ''}
        minutesRead={featuredPost?.readingTime as number}
        href={`/blog/${categoryName}/${featuredPost?.slug}`}
        tag={featuredPost?.category?.name || ''}
        picture={featuredImage || overwrittenMediumImage}
      />
      <hr className={styles['border']} />
      <div className={styles['main-content']}>
        <section>
          <h2 className={styles['browse-category-title']}>Browse by category</h2>
          <div className={styles['categories-container']}>
            {highlightedCategories.map(tag => (
              <Button key={tag.label} href={tag.href} prefetch={false}>
                {tag.label}
              </Button>
            ))}
            {!isLoadingCategories && Boolean(moreOptions.items[0].items.length) && (
              <MenuDropdown
                trigger={TriggerButton}
                {...moreOptions}
                side="bottom"
                align="start"
                sideOffset={8}
              />
            )}
          </div>
        </section>
        <section className={styles['latest-news-section']}>
          <CategoryTitleButton href="blog/latest-news">Latest news</CategoryTitleButton>
          <ArticleList
            className={styles['latest-news-article-list']}
            posts={formatLatestNewsPosts}
          />
        </section>
        <StayInTheLoop />
        <section className={styles['article-section']}>
          {sectionsAndPosts.map(
            category =>
              Boolean(category.posts.length) && (
                <div key={`category-${category.id}`}>
                  <CategoryTitleButton href={`blog/${category.id}`}>
                    {category.name || ''}
                  </CategoryTitleButton>
                  <ArticleList
                    className={styles['article-list']}
                    posts={category.posts}
                    hideThirdCard
                    onLoadMore={onLoadMore}
                    hideCategoryTag
                  />
                </div>
              )
          )}
          {isFetchingNextPage && (
            <div className={styles['spinner-container']}>
              <Spinner size="large" />
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

export const getServerSideProps: GetServerSideProps = async () => {
  try {
    const queryClient = new QueryClient()
    const fetchGetBlogMainPage = queryClient.prefetchQuery(
      ['GetBlogMainPage', GET_BLOG_MAIN_PAGE_PAYLOAD],
      useGetBlogMainPageQuery.fetcher(GET_BLOG_MAIN_PAGE_PAYLOAD)
    )

    const fetchGetAllBlogCategoriesWithNestedPosts = queryClient.prefetchInfiniteQuery(
      [
        'GetAllBlogCategoriesWithNestedPosts.infinite',
        GET_ALL_BLOG_CATEGORIES_WITH_NESTED_POSTS_PAYLOAD,
      ],
      useGetAllBlogCategoriesWithNestedPostsQuery.fetcher(
        GET_ALL_BLOG_CATEGORIES_WITH_NESTED_POSTS_PAYLOAD
      )
    )

    const fetchGetAllBlogPosts = queryClient.prefetchInfiniteQuery(
      ['GetAllBlogPosts.infinite', GET_ALL_BLOG_POSTS_PAYLOAD],
      useGetAllBlogPostsQuery.fetcher(GET_ALL_BLOG_POSTS_PAYLOAD)
    )

    const fetchGetAllBlogPostsCategories = queryClient.prefetchInfiniteQuery(
      ['GetAllBlogCategories.infinite', GET_ALL_BLOG_CATEGORIES],
      useGetAllBlogCategoriesQuery.fetcher(GET_ALL_BLOG_CATEGORIES)
    )

    await Promise.all([
      fetchGetBlogMainPage,
      fetchGetAllBlogCategoriesWithNestedPosts,
      fetchGetAllBlogPosts,
      fetchGetAllBlogPostsCategories,
    ])

    // Parse and stringify based on:
    // https://github.com/TanStack/query/issues/1458#issuecomment-747716357
    return {
      props: {
        dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
      },
    }
  } catch (error) {
    const errorMessage = error as { message: string }
    Sentry.captureMessage(errorMessage.message)
    console.error(error)
    return {
      notFound: true,
    }
  }
}

export default Blog

Blog.getLayout = (page: ReactElement) => {
  return (
    <WebsiteLayout shouldChangeBgColor headerInitialTheme={HEADER_THEMES.NEUTRAL_BACKGROUND}>
      {page}
    </WebsiteLayout>
  )
}
